import {
    Component,
    Input,
    Output,
    EventEmitter,
    ViewChild,
    OnInit,
    SimpleChanges,
    OnChanges,
    AfterViewInit,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { ElementRef } from "@angular/core";
import { Observable, of, combineLatest } from "rxjs";
import { OnboardingGuideComponent } from "@/components/shared/onboarding-guide/onboarding-guide.component";
import { OnboardingStepResponse } from "@/_models/onboarding-step-response.interface";
import { SubscriptionService, UserService } from "@/_services";
import { OnboardingGuideService } from "@/_services/onboarding-guide.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { ResponsiveService } from "@/components/shared/responsive/responsive.service";
import { map, switchMap, filter, shareReplay } from "rxjs/operators";
import { OnboardingDismissalStorage } from "@/_services/storage";
@UntilDestroy()
@Component({
    selector: "app-setup-guide-button",
    standalone: true,
    imports: [CommonModule, OnboardingGuideComponent],
    templateUrl: "./setup-guide-button.component.html",
    styleUrls: ["./setup-guide-button.component.scss"],
})
export class SetupGuideButtonComponent
    implements OnInit, OnChanges, AfterViewInit
{
    @Input() showButton: boolean = true;
    @Input() asapFormId$?: Observable<string | null>;

    @Output() dismiss = new EventEmitter<void>();

    @ViewChild("onboardingGuide")
    onboardingGuide?: OnboardingGuideComponent;

    @ViewChild("desktopButtonRef")
    desktopButtonRef?: ElementRef;
    @ViewChild("mobileButtonRef")
    mobileButtonRef?: ElementRef;

    isTrialAccount$!: Observable<boolean>;
    isAsapAccount$!: Observable<boolean>;
    steps$: Observable<OnboardingStepResponse[]> = of([]);
    guideTitle$: Observable<string> = of("Onboarding Guide");
    stepsArray: number[] = [];
    completedSteps: number = 0;
    maxSteps: number = 0;
    isMobile: boolean = false;
    currentAsapFormId: string | null = null;

    constructor(
        private responsiveService: ResponsiveService,
        private onboardingGuideService: OnboardingGuideService,
        private userService: UserService,
        private subscriptionService: SubscriptionService,
    ) {}

    ngOnInit(): void {
        this.checkInitialScreenSize();
        this.initIsTrialStream();
        this.initGuideListeners();
    }

    ngAfterViewInit(): void {
        this.initAutoOpenBehavior();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes["asapFormId$"] && this.asapFormId$) {
            this.asapFormId$
                .pipe(untilDestroyed(this))
                .subscribe((asapFormId) => {
                    this.currentAsapFormId = asapFormId;
                    this.refreshSteps();
                });
        }
    }

    private initIsTrialStream(): void {
        this.isTrialAccount$ = this.subscriptionService
            .isTrialAccount()
            .pipe(shareReplay(1));
    }

    private initGuideListeners(): void {
        this.initResponsiveListener();
        this.initStepsStreams();
        this.initAsapAccountStream();
        this.initGuideTitleStream();
        this.initGuideStateSync();
    }

    private checkInitialScreenSize(): void {
        if (this.responsiveService.isMobile !== undefined) {
            this.isMobile = this.responsiveService.isMobile;
        }
    }

    private initResponsiveListener(): void {
        this.responsiveService.responsiveChangeSubject
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                this.isMobile = this.responsiveService.isMobile;
            });
    }

    private initStepsStreams(): void {
        this.steps$ = this.onboardingGuideService.steps$.pipe(shareReplay(1));

        const asapFormId$ = this.asapFormId$ || of(null);

        asapFormId$
            .pipe(
                switchMap((asapFormId) => {
                    this.currentAsapFormId = asapFormId;
                    return this.onboardingGuideService.getOnboardingSteps(
                        asapFormId,
                    );
                }),
                untilDestroyed(this),
            )
            .subscribe();

        this.steps$.pipe(untilDestroyed(this)).subscribe((steps) => {
            this.maxSteps = steps.length;
            this.completedSteps = steps.filter((step) => step.completed).length;
            this.stepsArray = Array.from(
                { length: this.maxSteps },
                (_, i) => i + 1,
            );

            if (this.maxSteps > 0 && this.completedSteps === this.maxSteps) {
                OnboardingDismissalStorage.remove();
            }
        });
    }

    private initAsapAccountStream(): void {
        this.isAsapAccount$ = this.steps$.pipe(
            map((steps) => {
                if (!steps || steps.length === 0) {
                    return false;
                }
                return steps.some(
                    (s) =>
                        s.step === "setupRecipients" ||
                        s.step === "shareAccess",
                );
            }),
            shareReplay(1),
        );
    }

    private initGuideTitleStream(): void {
        this.guideTitle$ = this.steps$.pipe(
            map((steps) => {
                if (!steps || steps.length === 0) {
                    return "Onboarding Guide";
                }
                const isAsap = steps.some(
                    (s) =>
                        s.step === "setupRecipients" ||
                        s.step === "shareAccess",
                );
                return isAsap ? "Quick Start Guide" : "Account Setup";
            }),
        );
    }

    private initGuideStateSync(): void {
        combineLatest([this.onboardingGuideService.guideState$, this.steps$])
            .pipe(
                filter(([state, steps]) => state.isOpen && steps.length > 0),
                untilDestroyed(this),
            )
            .subscribe(([state]) => {
                if (this.onboardingGuide) {
                    const syntheticEvent = this.createSyntheticEvent(
                        state.event,
                    );
                    if (syntheticEvent.target) {
                        this.onboardingGuide.toggle(syntheticEvent);
                    }
                }
            });
    }

    private initAutoOpenBehavior(): void {
        let hasAutoOpened = false;
        let wasOpen = false;

        this.onboardingGuideService.guideState$
            .pipe(untilDestroyed(this))
            .subscribe((state) => {
                if (wasOpen && !state.isOpen) {
                    OnboardingDismissalStorage.put(true);
                }
                wasOpen = state.isOpen;
            });

        this.steps$
            .pipe(
                filter((steps) => steps.length > 0),
                untilDestroyed(this),
            )
            .subscribe((steps) => {
                const isDismissed = OnboardingDismissalStorage.get();

                if (hasAutoOpened || isDismissed) return;

                const hasIncompleteSteps = steps.some(
                    (step) => !step.completed,
                );
                if (
                    hasIncompleteSteps &&
                    this.showButton &&
                    this.onboardingGuide &&
                    !this.isMobile
                ) {
                    hasAutoOpened = true;
                    const syntheticEvent = this.createSyntheticEvent();
                    if (syntheticEvent.target) {
                        this.onboardingGuide.toggle(syntheticEvent);
                        this.onboardingGuideService.open(syntheticEvent);
                    }
                }
            });
    }

    private refreshSteps() {
        this.onboardingGuideService
            .refresh(this.currentAsapFormId || undefined)
            .subscribe();
    }

    openGuide(event: Event) {
        if (this.onboardingGuide) {
            this.onboardingGuide.toggle(event);
        }
    }

    dismissGuide(event: Event) {
        event.stopPropagation();
        this.userService
            .dismissBanner("onboardingGuide", true)
            .subscribe(() => {
                this.dismiss.emit();
            });
    }

    onStepsChange(steps: OnboardingStepResponse[]) {
        this.onboardingGuideService.setSteps(steps);
        this.refreshSteps();
    }

    private createSyntheticEvent(sourceEvent?: Event): Event {
        const target = this.isMobile
            ? this.mobileButtonRef?.nativeElement
            : this.desktopButtonRef?.nativeElement;
        return {
            target,
            currentTarget: target,
            preventDefault: () => sourceEvent?.preventDefault?.(),
            stopPropagation: () => sourceEvent?.stopPropagation?.(),
        } as unknown as Event;
    }
}
